/**
 * Aico Wishlist — shared client (PDP, cards, recommendations)
 * Logged-in: /apps/wishlist proxy; guests: localStorage. Cache namespaced per principal, emits 'wishlist:change'
 */
(function () {
  'use strict';

  var CONFIG = window.AICO_WISHLIST_CONFIG || {};
  var CUSTOMER_ID = CONFIG.customerId != null ? CONFIG.customerId : (window.PDT_CUSTOMER_ID || null);
  var PROXY = (CONFIG.proxyBase || '/apps/wishlist').replace(/\/$/, '');
  var GUEST_KEY = 'aico_wishlist_guest';

  function cacheKey() {
    return CUSTOMER_ID ? 'aico_wishlist_user_' + CUSTOMER_ID : GUEST_KEY;
  }

  var items = [];
  var index = {};
  var isReady = false;
  var resolveReady;
  var readyPromise = new Promise(function (resolve) {
    resolveReady = resolve;
  });

  function vkey(v) {
    return String(v);
  }

  function rebuildIndex() {
    index = {};
    for (var i = 0; i < items.length; i++) {
      index[vkey(items[i].variantId)] = items[i];
    }
  }

  function emit() {
    window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: items.slice() } }));
  }

  /* ---- item shape ---- */

  function normalize(raw) {
    return {
      productId: String(raw.productId),
      variantId: String(raw.variantId),
      productTitle: raw.productTitle || '',
      productTitleTranslations:
        raw.productTitleTranslations && typeof raw.productTitleTranslations === 'object'
          ? raw.productTitleTranslations
          : null,
      price: raw.price != null ? String(raw.price) : '',
      variantTitle: raw.variantTitle || '',
      compareAtPrice: raw.compareAtPrice != null ? String(raw.compareAtPrice) : '',
      imageUrl: raw.imageUrl || '',
      productHandle: raw.productHandle || '',
      sku: raw.sku || '',
      available: raw.available !== false
    };
  }

  function isValid(item) {
    return item && item.productId && item.variantId && item.productTitle && item.price !== '';
  }

  /* ---- localStorage cache (fast paint + guest store) ---- */

  function readCache(key) {
    try {
      var parsed = JSON.parse(localStorage.getItem(key));
      return parsed && Array.isArray(parsed.items) ? parsed.items : [];
    } catch (e) {
      return [];
    }
  }

  function saveCache() {
    try {
      localStorage.setItem(cacheKey(), JSON.stringify({ version: 1, items: items }));
    } catch (e) {
      /* quota / private mode — ignore */
    }
  }

  function setItems(next) {
    items = (next || []).map(normalize);
    rebuildIndex();
    saveCache();
    emit();
  }

  /* ---- app proxy (logged-in only) ---- */

  function api(path, opts) {
    opts = opts || {};
    var headers = Object.assign({ Accept: 'application/json' }, opts.headers);
    if (opts.body) headers['Content-Type'] = 'application/json';
    opts.headers = headers;
    opts.credentials = 'same-origin';
    return fetch(PROXY + path, opts).then(function (res) {
      if (!res.ok) throw new Error('wishlist api ' + res.status);
      return res.json();
    });
  }

  /* ---- public ops ---- */

  function has(variantId) {
    return !!index[vkey(variantId)];
  }

  function getItems() {
    return items.slice();
  }

  function count() {
    return items.length;
  }

  function addLocal(item) {
    items = items.concat([item]);
    rebuildIndex();
    saveCache();
    emit();
  }

  function removeLocal(variantId) {
    var k = vkey(variantId);
    items = items.filter(function (it) {
      return vkey(it.variantId) !== k;
    });
    rebuildIndex();
    saveCache();
    emit();
  }

  function add(raw) {
    var item = normalize(raw);
    if (!isValid(item)) {
      return Promise.reject(new Error('wishlist add: productId, variantId, productTitle, price are required'));
    }
    if (has(item.variantId)) return Promise.resolve(item);

    addLocal(item); // optimistic
    if (!CUSTOMER_ID) return Promise.resolve(item);

    return api('', { method: 'POST', body: JSON.stringify(item) })
      .then(function (r) {
        return (r && r.item) || item;
      })
      .catch(function (err) {
        removeLocal(item.variantId); // revert
        throw err;
      });
  }

  function remove(variantId) {
    var existing = index[vkey(variantId)];
    if (!existing) return Promise.resolve(false);

    removeLocal(variantId); // optimistic
    if (!CUSTOMER_ID) return Promise.resolve(true);

    return api('/' + encodeURIComponent(variantId), { method: 'DELETE' })
      .then(function () {
        return true;
      })
      .catch(function (err) {
        addLocal(existing); // revert
        throw err;
      });
  }

  function toggle(raw) {
    if (has(raw.variantId)) {
      return remove(raw.variantId).then(function () {
        return false;
      });
    }
    return add(raw).then(function () {
      return true;
    });
  }

  function clear() {
    var prev = items.slice();
    setItems([]);
    if (!CUSTOMER_ID) return Promise.resolve(true);

    return api('', { method: 'DELETE' })
      .then(function () {
        return true;
      })
      .catch(function (err) {
        setItems(prev); // revert
        throw err;
      });
  }

  /* ---- init / sync ---- */

  function finishReady() {
    isReady = true;
    resolveReady();
  }

  function init() {
    // Instant paint from this principal's own cache
    items = readCache(cacheKey()).map(normalize);
    rebuildIndex();

    if (!CUSTOMER_ID) {
      emit();
      finishReady();
      return;
    }

    // Logged in: merge any guest items once, then let the server overwrite the cache
    var guest = readCache(GUEST_KEY);
    var merge = guest.length
      ? api('/bulk', { method: 'POST', body: JSON.stringify({ items: guest.map(normalize) }) })
          .then(function () {
            try {
              localStorage.removeItem(GUEST_KEY);
            } catch (e) {
              /* ignore */
            }
          })
          .catch(function () {
            /* keep guest store if merge failed */
          })
      : Promise.resolve();

    merge
      .then(function () {
        return api('', { method: 'GET' });
      })
      .then(function (r) {
        setItems(r && r.items ? r.items : []); // server is the source of truth
      })
      .catch(function () {
        emit(); // offline / error: keep cached items
      })
      .then(finishReady);
  }

  window.AicoWishlist = {
    ready: function () {
      return readyPromise;
    },
    isReady: function () {
      return isReady;
    },
    has: has,
    getItems: getItems,
    count: count,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear
  };

  init();
})();
